import React, { useLayoutEffect, useRef, useCallback } from 'react';

import '../styles/truncatedContent.css';

const supportsInterpolateSize = typeof CSS !== "undefined" && CSS.supports?.("interpolate-size", "allow-keywords");

/* Unclamp, read the full height, apply the clamp, read the collapsed height, then restore the previous style and clamp state */
const measureHeights = (wrapper, content, maxLines) => {
    const wasClamped = wrapper.classList.contains("clamped");
    const previousStyle = content.getAttribute("style");

    wrapper.classList.remove("clamped");
    const fullHeight = content.scrollHeight;

    content.style.display = "-webkit-box";
    content.style.webkitBoxOrient = "vertical";
    content.style.webkitLineClamp = String(maxLines);
    content.style.overflow = "hidden";
    const collapsedHeight = content.clientHeight;

    if(previousStyle === null) {
        content.removeAttribute("style");
    }
    else {
        content.setAttribute("style", previousStyle);
    }
    wrapper.classList.toggle("clamped", wasClamped);

    return { fullHeight, collapsedHeight };
};

const TruncatedContent = ({
    expanded,
    lines: maxLines = 4,
    onOverflowChange = () => {},
    children
}) => {
    const wrapperRef = useRef(null);
    const contentRef = useRef(null);
    const clampTimeoutRef = useRef(null);

    const measure = useCallback(() => {
        const wrapper = wrapperRef.current;
        const container = contentRef.current;
        const content = container?.firstElementChild;
        if(!wrapper || !content) {
            return;
        }

        const { fullHeight, collapsedHeight } = measureHeights(wrapper, content, maxLines);
        const overflows = fullHeight > collapsedHeight + 1;
        onOverflowChange(overflows);

        /* Adds a small buffer on top of the measured height */
        const bufferedCollapsedHeight = collapsedHeight + 6;

        wrapper.style.setProperty("--truncate-lines", maxLines);
        wrapper.style.setProperty("--collapsed-max-height", `${bufferedCollapsedHeight}px`);

        /* Adds the clamp only after the collapse transition finishes */
        window.clearTimeout(clampTimeoutRef.current);
        if(overflows && !expanded) {
            const transitionDuration = (parseFloat(getComputedStyle(wrapper).transitionDuration) || 0) * 1000;
            clampTimeoutRef.current = window.setTimeout(() => wrapper.classList.add("clamped"), transitionDuration);
        }
        else {
            wrapper.classList.remove("clamped");
        }

        if(!supportsInterpolateSize) {
            wrapper.style.maxHeight = `${expanded ? fullHeight : bufferedCollapsedHeight}px`;
        }
    }, [expanded, maxLines, onOverflowChange]);

    useLayoutEffect(() => {
        measure();

        const container = contentRef.current;
        const content = container?.firstElementChild;
        if(!content) {
            return;
        }

        const observer = new ResizeObserver(measure);
        observer.observe(content);

        return () => {
            observer.disconnect();
            window.clearTimeout(clampTimeoutRef.current);
        };
    }, [measure]);

    return (
        <div
            ref={wrapperRef}
            className={`content-collapse ${expanded ? "expanded" : ""}`.trim()}
        >
            <div ref={contentRef}>
                { children }
            </div>
        </div>
    );
};

export default TruncatedContent;
